/* California Accessories — collection + search filtering, sorting and pagination without page
   reloads. <facet-filters> wraps the section; all inputs belong to form[data-facet-form] through
   the form="" attribute. Changes fetch the same URL with ?section_id= (Section Rendering API),
   swap every [data-facets-region], and push the clean URL to history. */
(function () {
  'use strict';
  const theme = (window.theme = window.theme || {});
  const cache = new Map();

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('form[data-facet-form]');
      if (!this.form) return;

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', e => {
        if (e.target.matches('[data-price-input]')) this.queue(700);
      });
      this.form.addEventListener('submit', e => { e.preventDefault(); this.queue(0); });
      // Enter inside a price box applies right away instead of submitting a full page load
      this.addEventListener('keydown', e => {
        if (e.key === 'Enter' && e.target.matches('[data-price-input]')) { e.preventDefault(); this.queue(0); }
        if (e.key === 'Escape' && this.panelOpen()) this.closePanel();
      });

      this.addEventListener('click', e => {
        const link = e.target.closest('a[data-facet-link]');
        if (link) {
          e.preventDefault();
          const isPage = !!link.closest('.pagination');
          this.load(link.href, { push: true, scroll: isPage });
          return;
        }
        if (e.target.closest('[data-facets-open]')) { this.openPanel(); return; }
        if (e.target.closest('[data-facets-close]')) { this.closePanel(); return; }
        const more = e.target.closest('[data-facet-more]');
        if (more) {
          const list = document.getElementById(more.getAttribute('aria-controls'));
          if (!list) return;
          const collapsed = list.classList.toggle('is-collapsed');
          more.setAttribute('aria-expanded', String(!collapsed));
          more.textContent = collapsed ? more.dataset.moreLabel || more.textContent : 'Show less';
        }
      });

      this.querySelectorAll('[data-facet-more]').forEach(b => { b.dataset.moreLabel = b.textContent.trim(); });

      this.onPop = () => this.load(window.location.href, { push: false });
      window.addEventListener('popstate', this.onPop);
      this.mq = matchMedia('(min-width: 901px)');
      this.onMq = () => { if (this.mq.matches) this.closePanel(); };
      this.mq.addEventListener ? this.mq.addEventListener('change', this.onMq) : this.mq.addListener(this.onMq);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      if (this.mq) this.mq.removeEventListener ? this.mq.removeEventListener('change', this.onMq) : this.mq.removeListener(this.onMq);
      document.body.classList.remove('no-scroll');
    }

    onChange(e) {
      const el = e.target;
      if (!(el.form === this.form || el.getAttribute('form') === this.form.id)) return;
      if (el.matches('[data-price-input]')) { this.queue(0); return; }
      this.queue(el.type === 'checkbox' ? 250 : 0);
    }

    queue(delay) {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.load(this.buildUrl(), { push: true }), delay);
    }

    buildUrl() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value === '' || value == null) return;
        params.append(key, value);
      });
      const action = this.form.getAttribute('action') || window.location.pathname;
      const qs = params.toString();
      return action + (qs ? '?' + qs : '');
    }

    async load(url, opts) {
      opts = opts || {};
      const clean = new URL(url, window.location.origin);
      clean.searchParams.delete('section_id');
      const fetchUrl = new URL(clean.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const key = fetchUrl.toString();

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();
      this.classList.add('is-loading');

      try {
        let html = cache.get(key);
        if (!html) {
          const res = await fetch(key, { signal: this._controller.signal });
          if (!res.ok) throw new Error(res.status);
          html = await res.text();
          cache.set(key, html);
        }
        this.render(html);
        if (opts.push) window.history.pushState({ facets: true }, '', clean.pathname + clean.search);
        if (opts.scroll) {
          const results = this.querySelector('[data-facets-region="results"]');
          if (results) {
            const top = results.getBoundingClientRect().top + window.scrollY - 150;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = clean.toString();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const activeId = active && this.contains(active) ? active.id : null;
      const openState = {};
      this.querySelectorAll('details[data-facet-id]').forEach(d => { openState[d.dataset.facetId] = d.open; });
      const expanded = [...this.querySelectorAll('[data-facet-more][aria-expanded="true"]')].map(b => b.getAttribute('aria-controls'));

      doc.querySelectorAll('[data-facets-region]').forEach(fresh => {
        const name = fresh.dataset.facetsRegion;
        const current = this.querySelector('[data-facets-region="' + name + '"]');
        if (current) current.replaceWith(fresh);
      });

      // keep what the shopper had open or expanded
      this.querySelectorAll('details[data-facet-id]').forEach(d => {
        if (d.dataset.facetId in openState) d.open = openState[d.dataset.facetId];
      });
      this.querySelectorAll('[data-facet-more]').forEach(b => {
        b.dataset.moreLabel = b.textContent.trim();
        if (expanded.indexOf(b.getAttribute('aria-controls')) > -1) {
          const list = document.getElementById(b.getAttribute('aria-controls'));
          if (list) list.classList.remove('is-collapsed');
          b.setAttribute('aria-expanded', 'true');
          b.textContent = 'Show less';
        }
      });
      // the open-panel button lives in the swapped results region
      const opener = this.querySelector('[data-facets-open]');
      if (opener) opener.setAttribute('aria-expanded', String(this.panelOpen()));

      if (activeId) {
        const again = document.getElementById(activeId);
        if (again) again.focus({ preventScroll: true });
      }
      if (theme.init) theme.init(this);
    }

    /* ---------- mobile panel ---------- */
    panel() { return this.querySelector('[data-facets-panel]'); }
    panelOpen() { const p = this.panel(); return !!(p && p.classList.contains('is-open')); }

    openPanel() {
      const p = this.panel();
      if (!p) return;
      this._opener = document.activeElement;
      p.classList.add('is-open');
      this.classList.add('facets-open');
      document.body.classList.add('no-scroll');
      const btn = this.querySelector('[data-facets-open]');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      const close = p.querySelector('.facets__close');
      if (close) setTimeout(() => close.focus(), 50);
    }

    closePanel() {
      const p = this.panel();
      if (!p || !p.classList.contains('is-open')) return;
      p.classList.remove('is-open');
      this.classList.remove('facets-open');
      document.body.classList.remove('no-scroll');
      const btn = this.querySelector('[data-facets-open]');
      if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus({ preventScroll: true }); }
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
